/** Top navigation + page container — the viobean-style chrome. Nav items
 * are role-derived here in ONE place; pages never decide visibility. */
import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { completeTour } from "../../api/auth";
import { useAuth } from "../../auth/AuthContext";
import { useI18n, useT } from "../../i18n";
import { CreditsBanner } from "../../features/learning/CreditsBanner";
import { NotificationsBell } from "../../features/notifications/NotificationsBell";
import { ProductTour } from "../tour/ProductTour";
import { buildTourSteps } from "../tour/tourSteps";

/** Every menu item the app can show. Exported so the guided tour is forced
 *  (by the compiler) to carry an explanation for each one. */
export type NavKey =
  | "nav.dashboard" | "nav.learn" | "nav.practice" | "nav.speak"
  | "nav.team" | "nav.access" | "nav.tenants" | "nav.billing"
  | "nav.revenue" | "nav.ai" | "nav.reference" | "nav.store" | "nav.orders"
  | "nav.classes" | "nav.aiUsage" | "nav.notebook";

export interface NavItem {
  to: string;
  key: NavKey;
}

/** School-admin surfaces: meaningless for a class of one. Hidden while the
 *  account is solo, and back the moment it has more than one seat. Billing
 *  is deliberately NOT here — it's the upgrade path, so hiding it behind an
 *  upgrade would be circular. */
const TEAM_MANAGEMENT: NavKey[] = ["nav.team", "nav.access", "nav.classes", "nav.aiUsage"];

/** A top-level menu entry: either a plain link (`to` set, no children) or a
 *  group that opens a short list. */
export interface NavGroup {
  /** i18n key for the label — a NavKey when the group collapsed to one link. */
  key: NavKey | NavGroupKey;
  to?: string;
  items: NavItem[];
}

export type NavGroupKey =
  | "nav.group.learn" | "nav.group.practice"
  | "nav.group.school" | "nav.group.account"
  | "nav.group.platform" | "nav.group.revenue" | "nav.group.ai";

/** How the flat menu folds up. Twelve top-level items is not a menu, it is a
 *  list — the client's words were "very clumsy". Order within a group is the
 *  order a learner meets them. */
// A group must never carry the same label as one of its children — the
// client's words: "under Practice we have one more Practice submenu, it is
// very confusing what to select". So the GROUPS are named for the activity
// and the CHILDREN for the thing itself.
const TENANT_GROUPS: { key: NavGroupKey; members: NavKey[] }[] = [
  { key: "nav.group.learn", members: ["nav.learn", "nav.reference", "nav.notebook"] },
  { key: "nav.group.practice", members: ["nav.practice", "nav.speak"] },
  { key: "nav.group.school", members: ["nav.team", "nav.classes", "nav.access"] },
  { key: "nav.group.account", members: ["nav.store", "nav.billing", "nav.aiUsage"] },
];

const PLATFORM_GROUPS: { key: NavGroupKey; members: NavKey[] }[] = [
  // A single member collapses to that link, which is what we want for
  // Tenants: it is the operator's main screen, not a menu.
  { key: "nav.group.platform", members: ["nav.tenants"] },
  { key: "nav.group.revenue", members: ["nav.orders", "nav.revenue"] },
  { key: "nav.group.ai", members: ["nav.ai", "nav.aiUsage"] },
];

/** The menu as it is actually shown: Dashboard, then groups.
 *
 *  Built FROM `navItemsFor`, so visibility stays decided in one place — a
 *  solo account loses Team/Classes/Access there, and the School group
 *  disappears here on its own. A group left with a single child is rendered
 *  as that child: a dropdown holding one link is worse than no dropdown. */
export function navGroupsFor(
  role: string | undefined,
  opts: { solo?: boolean } = {},
): NavGroup[] {
  const items = navItemsFor(role, opts);
  const byKey = new Map(items.map((i) => [i.key, i]));
  const layout = role === "super_admin" ? PLATFORM_GROUPS : TENANT_GROUPS;

  const groups: NavGroup[] = [];
  const dashboard = byKey.get("nav.dashboard");
  if (dashboard) groups.push({ key: dashboard.key, to: dashboard.to, items: [] });

  for (const group of layout) {
    const members = group.members
      .map((k) => byKey.get(k))
      .filter((i): i is NavItem => i !== undefined);
    if (members.length === 0) continue;
    if (members.length === 1) {
      groups.push({ key: members[0].key, to: members[0].to, items: [] });
    } else {
      groups.push({ key: group.key, items: members });
    }
  }
  return groups;
}

export function navItemsFor(
  role: string | undefined,
  opts: { solo?: boolean } = {},
): NavItem[] {
  if (opts.solo) {
    return navItemsFor(role).filter((i) => !TEAM_MANAGEMENT.includes(i.key));
  }
  return baseNavItemsFor(role);
}

function baseNavItemsFor(role: string | undefined): NavItem[] {
  if (role === "super_admin") {
    return [
      { to: "/dashboard", key: "nav.dashboard" },
      { to: "/tenants", key: "nav.tenants" },
      { to: "/orders", key: "nav.orders" },
      { to: "/revenue", key: "nav.revenue" },
      { to: "/ai-settings", key: "nav.ai" },
      { to: "/ai-usage", key: "nav.aiUsage" },
    ];
  }
  if (role === "tenant_admin") {
    return [
      { to: "/dashboard", key: "nav.dashboard" },
      { to: "/learn", key: "nav.learn" },
      { to: "/reference", key: "nav.reference" },
      { to: "/notebook", key: "nav.notebook" },
      { to: "/store", key: "nav.store" },
      { to: "/practice", key: "nav.practice" },
      { to: "/speak", key: "nav.speak" },
      { to: "/team", key: "nav.team" },
      { to: "/classes", key: "nav.classes" },
      { to: "/access", key: "nav.access" },
      { to: "/billing", key: "nav.billing" },
      { to: "/ai-usage", key: "nav.aiUsage" },
    ];
  }
  // Every other tenant role — learners first among them — lives on the
  // learning surface. NO Store: in a school the admin buys, and a pupil
  // who can't order shouldn't be shown a shop they'd only bounce off
  // (the API already refuses them). A SOLO learner is a tenant_admin, so
  // they keep the Store via the branch above.
  return [
    { to: "/dashboard", key: "nav.dashboard" },
    { to: "/learn", key: "nav.learn" },
    { to: "/reference", key: "nav.reference" },
    { to: "/notebook", key: "nav.notebook" },
    { to: "/practice", key: "nav.practice" },
    { to: "/speak", key: "nav.speak" },
  ];
}

/** One top-level menu that opens a short list.
 *
 *  `data-tour-group` carries every child key so the guided tour can spotlight
 *  the visible TRIGGER for an item that lives inside a closed panel — see
 *  tourSteps.ts, which targets both attributes. */
function NavDropdown({ group }: { group: NavGroup }) {
  const t = useT();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    // Clicking anywhere else, or Escape, closes it — a menu that traps the
    // pointer is the thing people hate about menus.
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const active = group.items.some((i) => location.pathname.startsWith(i.to));

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        data-tour-group={group.items.map((i) => i.key).join(" ")}
        className={`flex items-center gap-1 rounded-control px-3 py-2 text-sm font-semibold ${active ? "bg-brand-soft text-brand-dark" : "text-muted hover:text-ink"}`}
      >
        {t(group.key)}
        <span aria-hidden className="text-[0.6rem] leading-none">▾</span>
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 min-w-44 max-w-[calc(100vw-2rem)] rounded-card border border-line bg-surface p-1 shadow-lg">
          {group.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-tour={item.key}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block whitespace-nowrap rounded-control px-3 py-2 text-sm font-semibold ${isActive ? "bg-brand-soft text-brand-dark" : "text-muted hover:bg-canvas hover:text-ink"}`
              }
            >
              {t(item.key)}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export function AppShell() {
  const { user, logout, refresh } = useAuth();
  const { t, locale, setLocale } = useI18n();
  const navigate = useNavigate();

  const items = navItemsFor(user?.role, { solo: user?.is_solo });
  const groups = navGroupsFor(user?.role, { solo: user?.is_solo });

  // White-label the browser tab. index.html can only carry a static fallback,
  // so the tenant's own name is applied here — otherwise every school's staff
  // sees OUR product name in their tab, which is the same leak the client
  // reported for the header.
  useEffect(() => {
    document.title = user?.tenant_name?.trim() || t("app.name");
  }, [user?.tenant_name, t]);

  // Guided tour: runs once for a tenant user who hasn't seen it, and is
  // replayable from the "?" button. `dismissed` keeps it closed for the rest
  // of the session even if persisting the flag fails.
  const [menuOpen, setMenuOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!dismissed && user?.tenant_id && !user.tour_completed) setTourOpen(true);
  }, [user, dismissed]);

  const closeTour = () => {
    setTourOpen(false);
    setDismissed(true);
    // Remember it server-side so it doesn't re-run on their next device.
    completeTour()
      .then(refresh)
      .catch(() => undefined); // non-fatal: the tour just stays local
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const openTour = () => { setDismissed(false); setTourOpen(true); };
  const toggleLocale = () => setLocale(locale === "en" ? "de" : "en");

  return (
    <div className="min-h-screen">
      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
          <NavLink to="/dashboard" className="flex items-center gap-2 text-xl font-extrabold text-brand-dark">
            {user?.tenant_name ? (
              user.tenant_logo_url ? (
                <img src={user.tenant_logo_url} alt="" className="h-8 w-8 rounded-full object-cover shadow-sm" />
              ) : (
                <span aria-hidden className="bg-grad-hero grid h-8 w-8 place-items-center rounded-full text-sm font-bold text-white shadow-sm">
                  {user.tenant_name.trim().charAt(0).toUpperCase()}
                </span>
              )
            ) : (
              <span aria-hidden className="bg-grad-hero grid h-8 w-8 place-items-center rounded-full text-sm text-white shadow-sm">✓</span>
            )}
            <span>{user?.tenant_name ?? t("app.name").toLowerCase()}</span>
          </NavLink>
          {/* Desktop nav. Hidden below md: even five items plus the account
              controls cannot fit a phone header. */}
          <nav className="hidden items-center gap-1 md:flex">
            {groups.map((group) =>
              group.to !== undefined ? (
                <NavLink
                  key={group.key}
                  to={group.to}
                  data-tour={group.key}
                  className={({ isActive }) =>
                    `rounded-control px-3 py-2 text-sm font-semibold ${isActive ? "bg-brand-soft text-brand-dark" : "text-muted hover:text-ink"}`
                  }
                >
                  {t(group.key)}
                </NavLink>
              ) : (
                <NavDropdown key={group.key} group={group} />
              ),
            )}
          </nav>
          <div className="flex items-center gap-2 md:gap-3">
            {/* The secondary controls fold into the drawer on a phone; only
                the bell, the avatar and the menu button stay in the bar. */}
            {user?.tenant_id && (
              <button
                onClick={openTour}
                aria-label={t("tour.help")}
                title={t("tour.help")}
                className="hidden h-7 w-7 place-items-center rounded-full border border-line text-sm font-bold text-muted hover:border-brand hover:text-brand md:grid"
              >
                ?
              </button>
            )}
            <button
              aria-label={t("nav.switchLanguage")}
              onClick={toggleLocale}
              className="hidden rounded-control border border-line px-2 py-1 text-xs font-bold uppercase text-muted hover:text-ink md:block"
            >
              {locale}
            </button>
            <NotificationsBell />
            <NavLink
              to="/profile"
              aria-label={t("nav.profile")}
              title={t("nav.profile")}
              className="grid h-9 w-9 place-items-center rounded-full bg-brand-soft text-sm font-bold text-brand-dark hover:ring-2 hover:ring-brand/30"
            >
              {(user?.full_name || "?").charAt(0).toUpperCase()}
            </NavLink>
            <button onClick={handleLogout} className="hidden text-sm font-semibold text-muted hover:text-danger md:block">
              {t("nav.logout")}
            </button>
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-label={menuOpen ? t("nav.closeMenu") : t("nav.menu")}
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
              className="grid h-10 w-10 place-items-center rounded-control border border-line text-muted hover:border-brand hover:text-brand md:hidden"
            >
              <span aria-hidden className="flex w-5 flex-col gap-[3px]">
                <span className="h-[2px] w-full rounded-full bg-current" />
                <span className="h-[2px] w-full rounded-full bg-current" />
                <span className="h-[2px] w-full rounded-full bg-current" />
              </span>
            </button>
          </div>
        </div>
        {menuOpen && (
          <>
            {/* A dimmed backdrop, and a panel that sits OVER the page. The
                menu used to be part of the document flow, which pushed the
                whole dashboard down the screen — the client's words: "it
                should not scroll or push the page below". */}
            {/* aria-hidden: tapping outside is a convenience, not the
                accessible way to close. The burger toggles it and Escape
                closes it — a second control called "Close menu" would just
                make the menu ambiguous to a screen reader. */}
            <div
              aria-hidden
              onClick={() => setMenuOpen(false)}
              className="fixed inset-0 z-30 bg-ink/30 md:hidden"
            />
          <nav
            id="mobile-nav"
            className="fixed inset-x-0 top-16 z-40 max-h-[calc(100dvh-4rem)] overflow-y-auto border-t border-line bg-surface shadow-xl md:hidden"
          >
            <div className="mx-auto flex max-w-6xl flex-col gap-1 px-4 py-3">
              {/* No nested dropdowns on a phone — a group is a heading with
                  its links under it, all reachable in one scroll. */}
              {groups.map((group) =>
                group.to !== undefined ? (
                  <NavLink
                    key={group.key}
                    to={group.to}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      `rounded-control px-3 py-3 text-base font-semibold ${isActive ? "bg-brand-soft text-brand-dark" : "text-ink hover:bg-canvas"}`
                    }
                  >
                    {t(group.key)}
                  </NavLink>
                ) : (
                  <div key={group.key} className="mt-3 first:mt-0">
                    {/* The heading is a label, not a destination: smaller,
                        lettered out, on its own rule. Everything used to
                        look like one flat list of identical lines. */}
                    <p className="border-b border-line px-1 pb-1 text-[0.7rem] font-bold uppercase tracking-[0.12em] text-muted">
                      {t(group.key)}
                    </p>
                    {group.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={() => setMenuOpen(false)}
                        className={({ isActive }) =>
                          `mt-1 block rounded-control py-3 pl-4 pr-3 text-base font-semibold ${isActive ? "bg-brand-soft text-brand-dark" : "text-ink hover:bg-canvas"}`
                        }
                      >
                        {t(item.key)}
                      </NavLink>
                    ))}
                  </div>
                ),
              )}
              <div className="mt-2 flex items-center gap-3 border-t border-line pt-3">
                <button
                  aria-label={t("nav.switchLanguage")}
                  onClick={toggleLocale}
                  className="rounded-control border border-line px-3 py-2 text-sm font-bold uppercase text-muted hover:text-ink"
                >
                  {locale}
                </button>
                {user?.tenant_id && (
                  <button
                    onClick={() => { setMenuOpen(false); openTour(); }}
                    className="rounded-control border border-line px-3 py-2 text-sm font-semibold text-muted hover:border-brand hover:text-brand"
                  >
                    {t("tour.help")}
                  </button>
                )}
                <button onClick={handleLogout} className="ml-auto text-sm font-semibold text-muted hover:text-danger">
                  {t("nav.logout")}
                </button>
              </div>
            </div>
          </nav>
          </>
        )}
      </header>
      <main className="mx-auto max-w-6xl px-4 py-6 md:py-8">
        <CreditsBanner />
        <Outlet />
      </main>
      {tourOpen && (
        <ProductTour steps={buildTourSteps(t, items)} onClose={closeTour} />
      )}
    </div>
  );
}
